import React, { useEffect, useRef, useState } from 'react';
import { Coins, Heart, Loader2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { sendTip, listTips } from '../api/tips';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import InsufficientBalanceDialog from './InsufficientBalanceDialog';
import ReadOnlyNotice from './ReadOnlyNotice';
import { canWrite } from '../lib/canWrite';
import { tipChime } from '../lib/sounds';
import { CREATOR_PRICE_OPTIONS, fmtCredits } from '../lib/currency';

// Phase 30.B — tip presets use the same creator-pricing dropdown. The
// admin-configurable `tip_presets` setting still wins if present so admins
// can curate a shorter list for the UI.
const DEFAULT_PRESETS = CREATOR_PRICE_OPTIONS;

// Phase F4.2 — idempotency ref (same model as ChatTipDialog): stable across
// retries of the same attempt, regenerated only after a SUCCESSFUL tip.
const newClientRef = () =>
  (window.crypto?.randomUUID?.() || `ref_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);

/**
 * TipJar — fixed sidebar widget for sending tips during a live stream.
 * - Works on every live type (free / paid / audio / video).
 * - Owners see read-only totals.
 * - Auto-polls /tips every 4s to render a floating animation when new tips
 *   from other viewers land in the ring buffer.
 */
const TipJar = ({ live, currentUserId, onTipSent }) => {
  const { toast } = useToast();
  const { user, refresh: refreshUser } = useAuth();
  const isOwner = !!live.is_owner;
  const [amount, setAmount] = useState(DEFAULT_PRESETS[1] || 100);
  const [custom, setCustom] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [tips, setTips] = useState([]);
  const [floatingTips, setFloatingTips] = useState([]);
  const [insufficientOpen, setInsufficientOpen] = useState(false);
  const [attempted, setAttempted] = useState(0);
  const [presets, setPresets] = useState(DEFAULT_PRESETS);
  const seenRef = useRef(new Set());
  const clientRefRef = useRef(newClientRef());

  // Phase 17 — fetch dynamic tip presets from /public/config (Super Admin editable)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/config`);
        const cfg = await res.json();
        if (mounted && Array.isArray(cfg.tip_presets) && cfg.tip_presets.length > 0) {
          setPresets(cfg.tip_presets);
          setAmount(cfg.tip_presets[1] || cfg.tip_presets[0]);
        }
      } catch (_e) { /* keep defaults */ }
    })();
    return () => { mounted = false; };
  }, []);

  // Poll for new tips every 4s while the live is running
  useEffect(() => {
    if (live.status !== 'live') return undefined;
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const data = await listTips(live.id);
        if (cancelled) return;
        setTips(data.tips || []);
        // Detect new tips and animate them
        const fresh = (data.tips || []).filter((t) => !seenRef.current.has(t.id));
        fresh.forEach((t) => seenRef.current.add(t.id));
        if (fresh.length && seenRef.current.size > fresh.length) {
          // skip animation on the first poll (initial backlog)
          setFloatingTips((prev) => [...prev, ...fresh].slice(-6));
          // Remove each after 4s
          fresh.forEach((t) => {
            setTimeout(() => {
              setFloatingTips((curr) => curr.filter((x) => x.id !== t.id));
            }, 4000);
          });
        }
      } catch (_e) { /* ignore polling errors */ }
      timer = setTimeout(poll, 4000);
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [live.id, live.status]);

  const totalAmount = Number(live.total_tips_amount || 0);
  const totalCount = Number(live.total_tips_count || 0);
  // Phase 13: backend exposes whether the live's creator is a VIP and can receive tips.
  // Default to true if the field is absent (older clients / older live docs).
  const tipsEnabled = live.creator_tips_enabled !== false;

  const handleSend = async (value) => {
    if (isOwner) return;
    // Phase F4.2 — double click/tap guard: one in-flight tip at a time.
    if (sending) return;
    if (!canWrite(user)) {
      toast({ title: 'Verify your email first', description: 'You must confirm your email before sending tips.', variant: 'destructive' });
      return;
    }
    const v = Math.round(Number(value));
    // Phase F4.1 — validate against the ACTIVE presets (admin-configured).
    if (!v || !presets.includes(v)) {
      toast({
        title: 'Importo non valido',
        description: `Scegli uno dei valori: ${presets.map((c) => fmtCredits(c)).join(', ')}`,
        variant: 'destructive',
      });
      return;
    }
    // Phase 16/30.B — wallet pre-check (UX). Balances are now Credits (int).
    const balance = Number(user?.wallet_balance || 0);
    if (balance < v) {
      setAttempted(v);
      setInsufficientOpen(true);
      return;
    }
    setSending(true);
    try {
      const result = await sendTip(live.id, v, message, clientRefRef.current);
      // Phase F4.2 — success: next deliberate tip gets a fresh ref. A failed
      // attempt keeps the same ref so manual retries stay idempotent.
      clientRefRef.current = newClientRef();
      tipChime();
      toast({
        title: `Tip inviato — ${fmtCredits(v)}`,
        description: `Netto al creator: ${fmtCredits(result.net_amount)}`,
      });
      setMessage('');
      setCustom('');
      refreshUser?.();
      onTipSent?.(result);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      // Server-side defence-in-depth — balance changed between snapshot and click
      if (e.response?.status === 402 || detail.startsWith('insufficient_balance|')) {
        setAttempted(v);
        setInsufficientOpen(true);
        refreshUser?.();
      } else {
        toast({
          title: 'Tip failed',
          description: detail.split('|').pop() || 'Try again',
          variant: 'destructive',
        });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative rounded-2xl border border-border bg-card p-4" data-testid="tip-jar">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/15 gold-text">
            <Coins className="h-4 w-4" />
          </div>
          <h3 className="text-sm font-semibold uppercase tracking-wide gold-text">Tip Jar</h3>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</p>
          <p className="text-sm font-semibold tabular-nums text-foreground" data-testid="tip-jar-total">
            {fmtCredits(totalAmount)}
            <span className="ml-1 text-[10px] text-muted-foreground">· {totalCount} tips</span>
          </p>
        </div>
      </div>

      {!isOwner && !tipsEnabled && (
        <div
          className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-center text-xs text-amber-200"
          data-testid="tips-disabled-banner"
        >
          This Voice Pro hasn’t enabled monetization yet — tips are temporarily unavailable.
        </div>
      )}

      {!isOwner && tipsEnabled && (
        <>
          <ReadOnlyNotice action="send tips" className="mb-2" />
          <div className="mb-2 grid grid-cols-3 gap-1.5">
            {presets.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setAmount(v) || setCustom('')}
                disabled={sending}
                className={`rounded-md py-1.5 text-xs font-semibold transition ${
                  amount === v && !custom
                    ? 'gold-gradient text-black'
                    : 'border border-border text-foreground hover:border-amber-500/50'
                }`}
                data-testid={`tip-preset-${v}`}
              >
                {fmtCredits(v)}
              </button>
            ))}
          </div>
          {/* Phase 30.B — no custom amount; the dropdown above is the only source. */}
          <Input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={140}
            placeholder="Aggiungi un messaggio (facoltativo)"
            className="mb-2 h-8 bg-background text-xs"
            data-testid="tip-message-input"
          />
          <Button
            onClick={() => handleSend(amount)}
            disabled={sending}
            className="h-9 w-full gold-gradient text-sm font-semibold"
            data-testid="tip-send-btn"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Heart className="mr-1.5 h-3.5 w-3.5" />
                Invia tip da {fmtCredits(amount)}
              </>
            )}
          </Button>
        </>
      )}

      {isOwner && (
        <p className="rounded-md bg-background/40 px-3 py-2 text-center text-xs text-muted-foreground">
          Gli spettatori possono sostenerti qui. Le tips arrivano nel tuo wallet al netto delle commissioni.
        </p>
      )}

      {/* Recent tips ticker */}
      {tips.length > 0 && (
        <div className="mt-3 border-t border-border pt-2" data-testid="tip-jar-recent">
          <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Recenti</p>
          <div className="max-h-32 space-y-1 overflow-y-auto">
            {[...tips].reverse().slice(0, 6).map((t) => (
              <div key={t.id} className="flex items-center justify-between text-[11px]">
                <span className="truncate text-foreground">
                  <span className="gold-text">@{t.from_username}</span>
                  {t.message ? ` — “${t.message}”` : ''}
                </span>
                <span className="ml-2 shrink-0 gold-text font-semibold">{fmtCredits(t.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Floating animation overlay (rises from bottom of stage) */}
      <div className="pointer-events-none fixed bottom-24 right-6 z-50 flex flex-col items-end gap-2">
        {floatingTips.map((t) => (
          <div
            key={t.id}
            className="flex items-center gap-2 rounded-full border border-amber-500/40 bg-card/95 px-3 py-1.5 shadow-2xl backdrop-blur tip-float"
            data-testid="tip-floating"
          >
            <Sparkles className="h-4 w-4 gold-text" />
            <span className="text-xs font-semibold text-foreground">
              <span className="gold-text">@{t.from_username}</span> tip {fmtCredits(t.amount)}
            </span>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes tipFloatRise {
          0%   { opacity: 0; transform: translateY(20px) scale(0.9); }
          12%  { opacity: 1; transform: translateY(0) scale(1.05); }
          22%  { transform: translateY(0) scale(1); }
          85%  { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-12px); }
        }
        .tip-float { animation: tipFloatRise 4s ease-out forwards; }
      `}</style>

      <InsufficientBalanceDialog
        open={insufficientOpen}
        onOpenChange={setInsufficientOpen}
        balance={Number(user?.wallet_balance || 0)}
        required={attempted}
        context="tip"
      />
    </div>
  );
};

export default TipJar;
